/**
 * Hero Feature Module
 * Handles video playback and lazy loading for hero feature sections
 * Supports responsive video sources and poster images
 */

interface HeroFeatureOptions {
  pauseOnScroll?: boolean;
  lazyLoadOffset?: number;
}

class HeroFeatureVideo {
  private element: HTMLElement;
  private video: HTMLVideoElement | null;
  private options: HeroFeatureOptions;
  private isPlaying: boolean = false;
  private observer: IntersectionObserver | null = null;
  private mediaQueryList: MediaQueryList | null = null;

  constructor(element: HTMLElement, options: HeroFeatureOptions = {}) {
    this.element = element;
    this.video = element.querySelector('.module__background-video');
    this.options = {
      pauseOnScroll: true,
      lazyLoadOffset: 0.1,
      ...options
    };

    if (this.video) {
      this.init();
    }
  }

  private init(): void {
    // Set up intersection observer for play/pause on scroll
    if (this.options.pauseOnScroll && 'IntersectionObserver' in window) {
      this.setupIntersectionObserver();
    }

    // Handle video load errors
    if (this.video) {
      this.video.addEventListener('error', this.handleVideoError.bind(this));

      // Ensure video plays when it can
      this.video.addEventListener('canplay', () => {
        if (this.isInViewport() && !this.isPlaying) {
          this.playVideo();
        }
      });
    }

    // Handle reduced motion preference
    if (this.prefersReducedMotion()) {
      this.pauseVideo();
    }

    // Set up responsive poster switching if data attributes exist
    this.setupResponsivePoster();
  }

  private setupResponsivePoster(): void {
    if (!this.video) return;

    const desktopPoster = this.video.getAttribute('data-poster-desktop');
    const mobilePoster = this.video.getAttribute('data-poster-mobile');

    // Only set up if we have different posters for desktop and mobile
    if (desktopPoster && mobilePoster && desktopPoster !== mobilePoster) {
      // Create media query for 768px breakpoint
      this.mediaQueryList = window.matchMedia('(min-width: 769px)');

      // Initial poster update
      this.updatePoster(this.mediaQueryList.matches);

      // Listen for viewport changes
      if (this.mediaQueryList.addEventListener) {
        this.mediaQueryList.addEventListener('change', (e) => {
          this.updatePoster(e.matches);
        });
      } else {
        // Fallback for older browsers
        this.mediaQueryList.addListener((e) => {
          this.updatePoster(e.matches);
        });
      }
    }
  }

  private updatePoster(isDesktop: boolean): void {
    if (!this.video) return;

    const desktopPoster = this.video.getAttribute('data-poster-desktop');
    const mobilePoster = this.video.getAttribute('data-poster-mobile');

    if (isDesktop && desktopPoster) {
      this.video.poster = desktopPoster;
    } else if (!isDesktop && mobilePoster) {
      this.video.poster = mobilePoster;
    }
  }

  private setupIntersectionObserver(): void {
    const options = {
      threshold: [ 0, this.options.lazyLoadOffset as number, 0.5, 1 ]
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > (this.options.lazyLoadOffset as number)) {
          this.playVideo();
        } else {
          this.pauseVideo();
        }
      });
    }, options);

    this.observer.observe(this.element);
  }

  private playVideo(): void {
    if (this.video && !this.isPlaying && !this.prefersReducedMotion()) {
      this.video.play().catch(error => {
        console.warn('Video autoplay failed:', error);
        // Autoplay might be blocked, handle gracefully
        this.showFallbackImage();
      });
      this.isPlaying = true;
    }
  }

  private pauseVideo(): void {
    if (this.video && this.isPlaying) {
      this.video.pause();
      this.isPlaying = false;
    }
  }

  private handleVideoError(): void {
    console.error('Video failed to load');
    this.showFallbackImage();
  }

  private showFallbackImage(): void {
    // Hide video and show fallback image if it exists
    if (this.video) {
      this.video.style.display = 'none';
      const fallbackPicture = this.element.querySelector('.module__background-picture');
      if (fallbackPicture) {
        (fallbackPicture as HTMLElement).style.display = 'block';
      }
    }
  }

  private isInViewport(): boolean {
    const rect = this.element.getBoundingClientRect();
    return (
      rect.top < window.innerHeight &&
      rect.bottom > 0
    );
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  public destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.video) {
      this.video.removeEventListener('error', this.handleVideoError);
      this.video.removeEventListener('canplay', () => {});
    }
    if (this.mediaQueryList && this.mediaQueryList.removeEventListener) {
      this.mediaQueryList.removeEventListener('change', () => {});
    }
  }
}

type Elements = {
  modules: HTMLElement[];
};

type HeroFeature = {
  elements: Elements;
  instances: HeroFeatureVideo[];
  init: () => void;
  bindEvents: () => void;
  initializeVideos: () => void;
  destroy: () => void;
};

const heroFeature: HeroFeature = {
  elements: {
    modules: []
  },
  instances: [],

  init() {
    // Select all hero feature modules that contain videos
    this.elements.modules = Array.from(
      document.querySelectorAll<HTMLElement>('.module--hero-feature:has(.module__background-video)')
    );

    // Check if hero feature exists and add class to body
    const heroFeatureModule = document.querySelector('.module--hero-feature');
    if (heroFeatureModule) {
      // Check if it's the first module on the page
      const firstModule = document.querySelector('.module');
      if (firstModule === heroFeatureModule) {
        document.body.classList.add('has-hero-feature');
      }
    }

    this.bindEvents();
  },

  bindEvents() {
    if (this.elements.modules.length > 0) {
      this.initializeVideos();
    }
  },

  initializeVideos() {
    this.elements.modules.forEach(module => {
      const instance = new HeroFeatureVideo(module);
      this.instances.push(instance);
    });
  },

  destroy() {
    this.instances.forEach(instance => {
      instance.destroy();
    });
    this.instances = [];
  }
};

export default heroFeature;
